import type { MediaModel } from 'src/types/media';
import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useState, useEffect } from 'react';

import Grid from '@mui/material/Grid';
import {
  Box,
  Card,
  Stack,
  Radio,
  Button,
  MenuItem,
  Typography,
  RadioGroup,
  InputAdornment,
  FormControlLabel,
} from '@mui/material';

import { getCurrencySymbol, getMainCurrencies } from 'src/utils/currency';

import { Field, RHFDatePicker } from 'src/components/hook-form';

import { LinkTypeEnum } from 'src/types/media';

import type { NewOrderSchemaType } from './order-new-edit-form';

// Define word count options and their associated costs
const WORD_COUNT_OPTIONS = [
  { value: 600, label: '600 palabras', additionalCost: 0 },
  { value: 800, label: '800 palabras', additionalCost: 0 },
  { value: 1000, label: '1000 palabras', additionalCost: 0 },
];

interface OrderDetailsProps {
  methods: UseFormReturn<NewOrderSchemaType>;
  mediaOptions: MediaModel[];
  loadingMedia: boolean;
  categoryOptions: any[];
  loadingCategories: boolean;
  onSearchMedia: (value: string) => void;
  selectedPlatformId?: number; // ID de la plataforma seleccionada
  isEditMode?: boolean; // Nuevo prop para saber si estamos en modo edición de pedido
  isEditingItem?: boolean; // Prop para saber si estamos editando un item del pack
  onCancelEditItem?: () => void; // Callback para cancelar la edición del item
}

export default function OrderDetails({
  methods,
  mediaOptions,
  loadingMedia,
  loadingCategories,
  onSearchMedia,
  selectedPlatformId,
  isEditMode = false,
  isEditingItem = false,
  onCancelEditItem,
}: OrderDetailsProps) {
  const requireRedaction = methods.watch('requireRedaction');
  const mediaId = methods.watch('mediaId');
  const categoryId = methods.watch('categoryId');
  const price = methods.watch('price');
  const costPrice = methods.watch('costPrice');

  // Estados para manejar categorías filtradas y comisión
  const [filteredCategories, setFilteredCategories] = useState<any[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<any>(null);
  const [hasCategoryCommission, setHasCategoryCommission] = useState(false);
  const [categoryCommission, setCategoryCommission] = useState(0);

  useEffect(() => {
    if (mediaId) {
      // Buscar el medio seleccionado
      const selectedMedia = mediaOptions.find((media) => media.id === mediaId.value);

      // Establecer automáticamente el precio del medio cuando se selecciona (solo en modo creación, no cuando editamos un item)
      if (selectedMedia && !isEditMode && !isEditingItem) {
        methods.setValue('price', selectedMedia.userPrice ?? 0);
      }

      if (selectedMedia && selectedMedia.categories && selectedMedia.categories.length > 0) {
        // Mostrar todas las categorías del medio seleccionado
        const mediaCategories = selectedMedia.categories.map((cat: any) => ({
          id: cat.categoryId,
          name: cat.categoryName,
          commission: cat.commision,
        }));

        setFilteredCategories(mediaCategories);

        // Solo resetear la categoría si no estamos en modo edición (ni de pedido ni de item) y la actual no está en las categorías del medio
        if (!isEditMode && !isEditingItem) {
          const mediaCategoryIds = mediaCategories.map((cat: any) => cat.id);
          const currentCategoryId = methods.getValues('categoryId');
          if (currentCategoryId && !mediaCategoryIds.includes(currentCategoryId)) {
            methods.setValue('categoryId', null, { shouldValidate: false, shouldTouch: false });
            setSelectedCategory(null);
            setHasCategoryCommission(false);
            setCategoryCommission(0);
          }
        }
      } else {
        // Si el medio no tiene categorías, limpiar pero solo si había una categoría seleccionada y no estamos en modo edición
        setFilteredCategories([]);
        if (!isEditMode && !isEditingItem) {
          const currentCategoryId = methods.getValues('categoryId');
          if (currentCategoryId) {
            methods.setValue('categoryId', null, { shouldValidate: false, shouldTouch: false });
            setSelectedCategory(null);
            setHasCategoryCommission(false);
            setCategoryCommission(0);
          }
        }
      }
    } else {
      // Si no hay medio seleccionado, limpiar precio y categorías (solo en modo creación, no cuando editamos un item)
      if (!isEditMode && !isEditingItem) {
        methods.setValue('price', 0);
      }
      setFilteredCategories([]);
      if (!isEditMode && !isEditingItem) {
        const currentCategoryId = methods.getValues('categoryId');
        if (currentCategoryId) {
          methods.setValue('categoryId', null, { shouldValidate: false, shouldTouch: false });
          setSelectedCategory(null);
          setHasCategoryCommission(false);
          setCategoryCommission(0);
        }
      }
    }
  }, [mediaId, mediaOptions, methods, isEditMode, isEditingItem]);

  // Efecto para manejar la comisión de la categoría seleccionada
  useEffect(() => {
    if (categoryId) {
      // Buscar la categoría seleccionada
      const selectedCat = filteredCategories.find((cat) => cat.id === categoryId);
      setSelectedCategory(selectedCat);

      if (selectedCat && selectedCat.commission && selectedCat.commission > 0) {
        setHasCategoryCommission(true);
        setCategoryCommission(selectedCat.commission);
        methods.setValue('categoryCommission', selectedCat.commission);
      } else {
        setHasCategoryCommission(false);
        setCategoryCommission(0);
        methods.setValue('categoryCommission', 0);
      }
    } else {
      setSelectedCategory(null);
      setHasCategoryCommission(false);
      setCategoryCommission(0);
      methods.setValue('categoryCommission', 0);
    }
  }, [categoryId, filteredCategories, methods]);

  // Calcular automáticamente DirectProfit cuando cambien price o costPrice
  useEffect(() => {
    if (selectedPlatformId === 1 && price && costPrice && costPrice > 0) {
      const calculatedProfit = price - costPrice;
      methods.setValue('directProfit', calculatedProfit, { shouldValidate: false });
    } else if (selectedPlatformId === 1) {
      methods.setValue('directProfit', undefined, { shouldValidate: false });
    }
  }, [price, costPrice, selectedPlatformId, methods]);

  // Manejar cambio en la comisión manual
  const handleCommissionChange = (value: number) => {
    setCategoryCommission(value);
    methods.setValue('categoryCommission', value);
  };

  return (
    <Card sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Detalles del Pedido
      </Typography>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Field.Autocomplete
            name="mediaId"
            label="Medio"
            placeholder="Buscar media..."
            options={mediaOptions || []}
            value={
              mediaId
                ? mediaOptions.find((m) => m.id === mediaId.value) ||
                  // Si el medio seleccionado no está en las opciones actuales, crear un objeto temporal
                  // para que el Autocomplete pueda mostrar el valor seleccionado
                  { id: mediaId.value, name: mediaId.label, url: '' }
                : null
            }
            getOptionLabel={(option) => {
              if (option && typeof option === 'object') {
                // Para valores ya seleccionados con formato {value, label}
                if ('label' in option && option.label) return option.label;
                // Para opciones de la API con formato {id, name}
                if ('name' in option && option.name) return option.name;
              }
              return '';
            }}
            isOptionEqualToValue={(option, value) => {
              if (!option || !value) return false;

              // Comparar IDs directamente
              const optionId = option.id;
              const valueId =
                typeof value === 'object' && 'value' in value ? value.value : value.id;

              return optionId === valueId;
            }}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{option.name}</div>
                  <div style={{ fontSize: '0.8em', color: '#666' }}>{option.url}</div>
                </div>
              </li>
            )}
            onChange={(_, newValue) => {
              if (newValue) {
                const formattedValue = {
                  value: newValue.id,
                  label: newValue.name,
                };
                methods.setValue('mediaId', formattedValue, { shouldValidate: true });
              } else {
                methods.setValue('mediaId', null, { shouldValidate: true });
              }
            }}
            onInputChange={(_, newInputValue, reason) => {
              // Solo actualizar la búsqueda cuando el usuario escribe (reason === 'input')
              // Ignorar 'reset' (cuando cambian las opciones) y 'clear' (cuando se limpia)
              if (reason === 'input') {
                onSearchMedia(newInputValue);
              }
            }}
            loading={loadingMedia}
            filterOptions={(options) => options} // Deshabilitar filtrado local, usar solo filtrado del backend
            freeSolo={false}
          />
        </Grid>

        {/* Price */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Field.Text
            name="price"
            label="Precio"
            placeholder="0.00"
            type="number"
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.75 }}>
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {getCurrencySymbol(methods.watch('currency'))}
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
          />
        </Grid>

        {/* Currency */}
        <Grid size={{ xs: 12, md: 2 }}>
          <Field.Select name="currency" label="Divisa" fullWidth>
            {getMainCurrencies().map((curr) => (
              <MenuItem key={curr.value} value={curr.value}>
                {curr.label}
              </MenuItem>
            ))}
          </Field.Select>
        </Grid>

        {/* % Descuento - En la misma fila */}
        <Grid size={{ xs: 12, md: 3 }}>
          <Field.Text
            name="discountPercentage"
            label="% Descuento"
            type="number"
            slotProps={{
              input: {
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
                inputProps: { min: 0, max: 100, step: 0.1 },
              },
            }}
          />
        </Grid>

        {/* Precio Coste - Siempre visible */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Field.Text
            name="costPrice"
            label="Precio Coste"
            placeholder="0.00"
            type="number"
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.75 }}>
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      {getCurrencySymbol(methods.watch('costPriceCurrency'))}
                    </Box>
                  </InputAdornment>
                ),
                inputProps: { min: 0, step: 0.01 },
              },
            }}
            helperText="Precio coste del pedido (opcional)"
          />
        </Grid>

        {/* Moneda Precio Coste */}
        <Grid size={{ xs: 12, md: 2 }}>
          <Field.Select name="costPriceCurrency" label="Divisa Coste" fullWidth>
            {getMainCurrencies().map((curr) => (
              <MenuItem key={curr.value} value={curr.value}>
                {curr.label}
              </MenuItem>
            ))}
          </Field.Select>
        </Grid>

        {/* ID Interno de Plataforma - Siempre visible */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Field.Text
            name="internalPlatformId"
            label="ID Interno de Plataforma"
            fullWidth
            helperText="ID interno alfanumérico de la plataforma (opcional)"/>
        </Grid>
       

        {/* Campos especiales para Kaboozt (Platform ID = 1) */}
        {selectedPlatformId === 1 && (
          <>
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mb: 2, mt: 3, color: 'primary.main' }}>
                Configuración Especial Kaboozt
              </Typography>
            </Grid>

          
            <Grid size={{ xs: 12, md: 12 }}>
              <Field.Text
                name="redactionUrl"
                label="URL de Redacción"
                fullWidth
                helperText="URL del documento de redacción"
              />
            </Grid>
          </>
        )}

        {/* Deadline Date */}
        <Grid size={{ xs: 12, md: 12 }}>
          <RHFDatePicker name="limitDate" label="Fecha Límite" minDate={dayjs()} key="limitDate" />
        </Grid>

        {/* Category y Comisión en la misma línea */}
        {/* Category - Now dependent on selected media */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Field.Select
            name="categoryId"
            label="Categoría"
            fullWidth
            disabled={loadingCategories || !mediaId}
            value={categoryId || ''}
          >
            {loadingCategories ? (
              <MenuItem disabled>Cargando categorías...</MenuItem>
            ) : !mediaId ? (
              <MenuItem disabled>Selecciona un medio primero</MenuItem>
            ) : filteredCategories.length === 0 ? (
              <MenuItem disabled>No hay categorías disponibles para este medio</MenuItem>
            ) : (
              filteredCategories.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))
            )}
          </Field.Select>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Field.Text
            name="categoryCommission"
            label="Extra Comisión"
            placeholder="0.00"
            type="number"
            value={categoryCommission}
            onChange={(e) => handleCommissionChange(Number(e.target.value))}
            disabled={!hasCategoryCommission}
            slotProps={{
              inputLabel: { shrink: true },
              input: {
                startAdornment: (
                  <InputAdornment position="start" sx={{ mr: 0.75 }}>
                    <Box component="span" sx={{ color: 'text.disabled' }}>
                      €
                    </Box>
                  </InputAdornment>
                ),
              },
            }}
            helperText={
              hasCategoryCommission
                ? `Comisión predeterminada: ${selectedCategory?.commission || 0}€`
                : 'No hay comisión para esta categoría'
            }
          />
        </Grid>

        {/* Final URL */}
        <Grid size={{ xs: 12 }}>
          <Field.Text name="finalUrl" label="URL Final" fullWidth />
        </Grid>

        {/* Requires Writing */}
        <Grid size={{ xs: 12 }}>
          <Field.Checkbox name="requireRedaction" label="Requiere Redacción" />
        </Grid>

        {/* Conditional fields based on requireRedaction */}
        {requireRedaction ? (
          <>
            {/* TODOS LOS ENLACES AGRUPADOS */}

            {/* Sección de enlaces - Título */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 2 }}>
                Enlaces a incluir en el artículo
              </Typography>
            </Grid>

            {/* URL 1, Keyword 1, Link Type 1 */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="url1" label="URL 1" fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="keyword1" label="Palabra Clave 1" fullWidth required />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Select name="linkType1" label="Tipo de Enlace 1" fullWidth required>
                {Object.entries(LinkTypeEnum)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  ))}
              </Field.Select>
            </Grid>

            {/* URL 2, Keyword 2, Link Type 2 */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="url2" label="URL 2" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="keyword2" label="Palabra Clave 2" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Select name="linkType2" label="Tipo de Enlace 2" fullWidth>
                {Object.entries(LinkTypeEnum)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  ))}
              </Field.Select>
            </Grid>

            {/* URL 3, Keyword 3, Link Type 3 */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="url3" label="URL 3" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="keyword3" label="Palabra Clave 3" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Select name="linkType3" label="Tipo de Enlace 3" fullWidth>
                {Object.entries(LinkTypeEnum)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  ))}
              </Field.Select>
            </Grid>

            {/* URL 4, Keyword 4, Link Type 4 */}
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="url4" label="URL 4" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Text name="keyword4" label="Palabra Clave 4" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <Field.Select name="linkType4" label="Tipo de Enlace 4" fullWidth>
                {Object.entries(LinkTypeEnum)
                  .filter(([key]) => isNaN(Number(key)))
                  .map(([key, value]) => (
                    <MenuItem key={value} value={value}>
                      {key}
                    </MenuItem>
                  ))}
              </Field.Select>
            </Grid>

            {/* Sección de información de redacción - Título */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                Detalles para la redacción
              </Typography>
            </Grid>

            {/* Order Details */}
            <Grid size={{ xs: 12 }}>
              <Field.Text
                name="orderDetail"
                label="Detalles del Pedido"
                multiline
                rows={4}
                fullWidth
              />
            </Grid>

            {/* Title */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="extraTitle" label="Título" fullWidth />
            </Grid>

            {/* Terms to Avoid */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="extraTermsToAvoid" label="Términos a Evitar" fullWidth />
            </Grid>

            {/* HTML Code */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="extraHtml" label="Código HTML" multiline rows={4} fullWidth />
            </Grid>

            {/* Information Source */}
            <Grid size={{ xs: 12 }}>
              <Field.Text name="extraSource" label="Fuente de Información" fullWidth />
            </Grid>

            {/* Word Count Selection */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, mt: 2 }}>
                Cantidad de palabras
              </Typography>
              <RadioGroup
                value={methods.watch('wordCount')}
                onChange={(e) => {
                  methods.setValue('wordCount', Number(e.target.value), { shouldValidate: true });
                }}
                row
              >
                {WORD_COUNT_OPTIONS.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio />}
                    label={`${option.label}`}
                  />
                ))}
              </RadioGroup>
            </Grid>

            {/* Images section - Title */}
            <Grid size={{ xs: 12 }}>
              <Typography variant="subtitle2" sx={{ mt: 3, mb: 2 }}>
                Imágenes para el artículo
              </Typography>
            </Grid>

            {/* Image 1 & Image 2 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Imagen principal
              </Typography>
              <Field.Upload
                name="image"
                placeholder="Subir imagen 1"
                helperText="Formatos aceptados: JPG, PNG, WEBP"
                sx={{
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Imagen secundaria
              </Typography>
              <Field.Upload
                name="extraImage"
                placeholder="Subir imagen 2"
                helperText="Formatos aceptados: JPG, PNG, WEBP"
                sx={{
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                }}
              />
            </Grid>
          </>
        ) : (
          <>
            {/* File Selector */}
            <Grid size={{ xs: 12 }}>
              <Field.Upload
                accept={{
                  'application/pdf': [],
                  'application/msword': [],
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
                  'text/plain': [],
                }}
                name="file"
                placeholder="Archivo"
              />
            </Grid>

            {/* Image 1 & Image 2 */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Imagen principal
              </Typography>
              <Field.Upload
                name="image"
                placeholder="Subir imagen principal"
                helperText="Formatos aceptados: JPG, PNG, WEBP"
                sx={{
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Imagen secundaria
              </Typography>
              <Field.Upload
                name="extraImage"
                placeholder="Subir imagen secundaria"
                helperText="Formatos aceptados: JPG, PNG, WEBP"
                sx={{
                  border: '1px dashed',
                  borderColor: 'primary.main',
                  borderRadius: 1,
                }}
              />
            </Grid>
          </>
        )}
      </Grid>

      {!isEditMode && (
        <Stack direction="row" justifyContent="flex-end" spacing={2} sx={{ mt: 3 }}>
          {isEditingItem && onCancelEditItem && (
            <Button variant="outlined" color="inherit" onClick={onCancelEditItem}>
              Cancelar
            </Button>
          )}
          <Button variant="contained" color={isEditingItem ? 'warning' : 'primary'} type="submit">
            {isEditingItem ? 'Guardar Cambios' : 'Añadir al carrito'}
          </Button>
        </Stack>
      )}
    </Card>
  );
}
